/*
 * CAT-407 — the PTFW 3.0 motion contract for the entry point's collapse.
 *
 * Every number here is from the motion spec and is a contract, not a taste
 * call; the two exceptions are marked. They live apart from
 * `entryPointTokens.js` because those are the shapes and colours the two
 * presentational states own, and these are the choreography the *caller* owns —
 * `SetupJourneyWidget` is the only thing that knows both states exist at once.
 *
 * Two rules the spec is emphatic about, and that the curves below encode:
 *
 *   1. SPRING overshoots past its target. On a transform that reads as weight;
 *      on opacity or blur it reads as a wobble, because opacity 1.04 and
 *      blur(-0.3px) both clamp and then settle back. So SPRING carries
 *      transform and nothing else, and SOFT carries opacity, blur and colour.
 *   2. Both states stay mounted and only transform / opacity / filter move.
 *      Nothing here animates a layout property, so the collapse never reflows
 *      the page underneath it.
 */

/** Exit curve for the expanded card — accelerates away, never comes back. */
export const EASE_IN = "cubic-bezier(0.4, 0, 1, 1)";
/** The arrival curve. Transform only — see rule 1 above. */
export const SPRING = "cubic-bezier(0.34, 1.4, 0.64, 1)";
/** Opacity, blur and colour. Everything SPRING is not allowed to touch. */
export const SOFT = "cubic-bezier(0.32, 0.72, 0, 1)";

/** Collapsing: the card leaves fast, the pill arrives with weight. */
export const CARD_EXIT_MS = 140;
export const PILL_ENTER_MS = 420;

/** Expanding: the pill just fades, the card arrives with the same weight. */
export const PILL_EXIT_MS = 240;
export const CARD_ENTER_MS = 420;

/**
 * The pill's four children build in on these delays and unbuild in reverse.
 * The spec calls this the single biggest reason the collapse reads as
 * choreography rather than as a cross-fade, so the order is fixed:
 * glyph, title, counter, chevron.
 */
export const PILL_STAGGER_IN_MS = [0, 55, 95, 125];
export const PILL_STAGGER_OUT_MS = [60, 40, 20, 0];

/** NOT from the spec — how long each staggered child takes once it starts. */
export const PILL_PART_MS = 180;
/** NOT from the spec — how far each child travels as it builds in. */
export const PILL_PART_RISE_PX = 4;

/** The card dissolves as it compresses; the pill resolves out of a softer one. */
export const CARD_BLUR_PX = 6;
export const PILL_BLUR_PX = 4;

/**
 * Both surfaces scale about the glyph, not about their own centres, so the
 * card compresses *toward* where the pill is about to be instead of shrinking
 * into itself. 28px is the spec's value and matches the pill's glyph centre
 * (16px padding + a 28px disc's radius).
 */
export const COLLAPSE_ORIGIN = "28px top";

/** NOT from the spec — the scale each surface rests at while it is the hidden one. */
export const CARD_COLLAPSED_SCALE = 0.92;
export const PILL_EXPANDED_SCALE = 0.96;

/*
 * ---------------------------------------------------------------------------
 * CAT-407 — the screen-level motion: the journey as an overlay over Home.
 * ---------------------------------------------------------------------------
 *
 * The collapse above choreographs two states of one entry point. Everything
 * below choreographs two *screens*, and it lives here rather than in a second
 * file because it is the same vocabulary — the widget is still the thing that
 * both ends of the motion are anchored to, and the acknowledgement glow below
 * literally lands on it.
 *
 * The spec's claim, and the reason a route swap could not express this: the
 * journey travels 100% -> 0 while Home travels 0 -> -22% and stays visible
 * behind the incoming edge. The speed difference *is* the effect. Matched
 * speeds read as one sheet of paper sliding; different speeds read as depth.
 * A route swap unmounts Home on the first frame and leaves nothing to animate
 * against, which is why both screens have to be on the page at once.
 */

/** Both screens, one duration. They start together and end together. */
export const SCREEN_MS = 460;
/** The screen curve. Decelerating hard — the arrival, not the departure. */
export const SCREEN_EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

/**
 * How far Home travels while the journey covers it. Less than the journey's
 * own 100%, which is the whole point: the gap between the two speeds is the
 * depth cue.
 */
export const HOME_PUSH_PERCENT = -22;

/**
 * Cast by the journey's leading edge onto Home. Without it the two screens
 * read as adjacent panels; with it the journey is unambiguously *above* Home.
 */
export const SCREEN_EDGE_SHADOW = "-6px 0 24px rgba(20, 12, 14, 0.14)";

/*
 * The acknowledgement glow, fired once on the widget when the journey pops
 * back to Home.
 *
 * It starts lit and only ever decays. That is not a style choice: Home is back
 * under the widget roughly 30ms into a 460ms pop, so a glow that ramped up
 * would spend its entire rise hidden behind the outgoing journey and the
 * creator would see only the tail. Starting at full means the first thing they
 * see as Home resolves is the widget already lit.
 *
 * Filter only — a -3px lift was tried and removed, because a widget that moves
 * on return reads as a flinch rather than an acknowledgement.
 */
export const ACK_GLOW_MS = 1100;
/** Held at full for this long before the decay starts. */
export const ACK_GLOW_HOLD_MS = 260;
/** NOT from the spec — the lit value itself, in the entry point's own red. */
export const ACK_GLOW_FILTER =
  "drop-shadow(0 0 10px rgba(238, 77, 55, 0.45)) drop-shadow(0 2px 4px rgba(238, 77, 55, 0.25))";
/**
 * The decayed end of the same filter, not `none`. A filter list only
 * interpolates against a filter list of the same shape — animating to `none`
 * is a discrete swap in several engines, which would make the glow cut out
 * rather than fade.
 */
export const ACK_GLOW_FILTER_SPENT =
  "drop-shadow(0 0 10px rgba(238, 77, 55, 0)) drop-shadow(0 2px 4px rgba(238, 77, 55, 0))";
